import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import StackedBarChart from '../../atoms/StackedBarChart/StackedBarChart';
import ChartNoDataState from '../../atoms/ChartNoDataState/ChartNoDataState';
import Select from '../../atoms/Select/Select';
import Button from '../../atoms/Button/Button';
import DownloadFile from '../../atoms/Icon/DownloadFile';
import ArrowRight from '../../atoms/Icon/ArrowRight';
import ChartLegendOverflow from '../ChartLegendOverflow/ChartLegendOverflow';
import { ChartConfig } from '../../../utils/BaseChart/BaseChartOptions';
import {
    formatNumWithKMB,
    formatNumWithKMBTwoDecimalPlaces,
    formatFixedSpacingXAxisLabel,
    CHART_FONT_FAMILY_CSS,
} from '../../../utils/formatters';
import {
    getAggregatedChartTooltipInteraction,
    getChartColumnHighlightInteraction,
} from '../../../utils/chartAggregatedTooltip';
import { BAR_VALUE_LABEL_DOMAIN_PADDING, computeSingleAxisDomain } from '../../../utils/chartAxisAlignment';
import { useStackedBarChartFilters, type StackedBarChartRow } from './useStackedBarChartFilters';
import { EnhancedStackedBarChartProps } from './EnhancedStackedBarChart.types';
import ChartFilterBar, { ChartFilterColumn, CHART_FILTER_SELECT_CLASSNAME } from '../EnhancedBarChart/ChartFilterBar';
import { chartMultiSelectTagRender } from '../EnhancedBarChart/chartFilterMultiTagRender';
import { chartFilterDropdownRender } from '../EnhancedBarChart/chartFilterDropdownRender';

type StackedBarChartRowWithTotals = StackedBarChartRow & {
    stackTotal: number;
    showTotalLabel: boolean;
    /** When true, total label is placed below the stack (negative total); otherwise above (positive / zero). */
    totalMarkerBelow: boolean;
};

/** Add per-row stack total and which segment anchors the total label (one label per category). */
function enrichRowsWithStackTotals(rows: StackedBarChartRow[]): StackedBarChartRowWithTotals[] {
    if (rows.length === 0) return [];

    const byCategory = new Map<string, StackedBarChartRow[]>();
    for (const row of rows) {
        const list = byCategory.get(row.category) ?? [];
        list.push(row);
        byCategory.set(row.category, list);
    }

    const totalByCategory = new Map<string, number>();
    const totalLabelSegmentIndexByCategory = new Map<string, number>();

    for (const [category, group] of byCategory) {
        const values = group.map((r) => r.value);
        const stackTotal = values.reduce((a, b) => a + b, 0);
        totalByCategory.set(category, stackTotal);

        let cum = 0;
        const prefixes: number[] = [];
        for (const v of values) {
            cum += v;
            prefixes.push(cum);
        }
        if (prefixes.length === 0) {
            totalLabelSegmentIndexByCategory.set(category, -1);
            continue;
        }
        // G2 stackY stacks negative segments in reverse series order; totals need the outermost segment.
        let segIdx = -1;
        if (stackTotal < 0) {
            const firstNegIdx = values.findIndex((v) => v < 0);
            segIdx = firstNegIdx >= 0 ? firstNegIdx : values.length - 1;
        } else {
            const outer = Math.max(...prefixes);
            for (let i = 0; i < prefixes.length; i++) {
                if (Math.abs(prefixes[i] - outer) < 1e-9) segIdx = i;
            }
            if (segIdx < 0) segIdx = prefixes.length - 1;
        }
        totalLabelSegmentIndexByCategory.set(category, segIdx);
    }

    const indexWithinCategory = new Map<string, number>();
    return rows.map((row) => {
        const idx = indexWithinCategory.get(row.category) ?? 0;
        indexWithinCategory.set(row.category, idx + 1);
        const labelSegIdx = totalLabelSegmentIndexByCategory.get(row.category) ?? -1;
        const stackTotal = totalByCategory.get(row.category) ?? 0;
        return {
            ...row,
            stackTotal,
            totalMarkerBelow: stackTotal < 0,
            showTotalLabel: idx === labelSegIdx && labelSegIdx >= 0,
        };
    });
}

const STACK_BAR_COLORS = [
    '#5B50D1',
    '#DBC7A4',
    '#4ECDC4',
    '#FF6B6B',
    '#95E1D3',
    '#F38181',
    '#AA96DA',
    '#FCBAD3',
    '#A8D8EA',
    '#FFD93D',
];

const STACKED_BAR_LABEL_PADDING_TOP = 20;
const STACKED_BAR_LABEL_INSET_TOP = 40;
const STACKED_BAR_Y_AXIS_DOMAIN_PADDING = Math.min(0.38, BAR_VALUE_LABEL_DOMAIN_PADDING + 0.2);
const STACKED_FIXED_BAR_WIDTH_PX = 32;
const STACKED_FIXED_CATEGORY_GAP_PX = 72;
const STACKED_FIXED_CATEGORY_STRIDE_PX = STACKED_FIXED_BAR_WIDTH_PX + STACKED_FIXED_CATEGORY_GAP_PX;
const STACKED_X_SCALE_PADDING_RATIO = STACKED_FIXED_CATEGORY_GAP_PX / STACKED_FIXED_CATEGORY_STRIDE_PX;
const VERTICAL_CHART_WRAPPER_HORIZONTAL_PADDING = 40;
const VERTICAL_PRIMARY_AXIS_WIDTH_ESTIMATE = 80;

/**
 * Mirrors G2 v5 `stackY` transform: each category splits segments into negatives vs non-negatives;
 * negatives stack downward from `start`, positives stack upward from `start`.
 */
function g2AlignedStackExtentFromSegmentValues(values: number[], start = 0): { min: number; max: number } {
    let lo = start;
    let hi = start;
    const PG: number[] = [];
    const NG: number[] = [];
    values.forEach((raw, i) => {
        const y = Number(raw);
        if (!Number.isFinite(y)) return;
        if (y < 0) NG.push(i);
        else PG.push(i);
    });

    let ny = start;
    for (let k = NG.length - 1; k >= 0; k--) {
        const i = NG[k];
        const y = Number(values[i]);
        const bottom = ny;
        ny += y;
        lo = Math.min(lo, bottom, ny);
        hi = Math.max(hi, bottom, ny);
    }

    let py = start;
    for (const i of PG) {
        const y = Number(values[i]);
        if (y > 0) {
            const bottom = py;
            py += y;
            lo = Math.min(lo, bottom, py);
            hi = Math.max(hi, bottom, py);
        } else {
            lo = Math.min(lo, py);
            hi = Math.max(hi, py);
        }
    }

    return { min: lo, max: hi };
}

const EnhancedStackedBarChartV2: React.FC<EnhancedStackedBarChartProps> = (props) => {
    const {
        vizData,
        title: propTitle,
        description,
        showFilters = true,
        onDownload,
        onBarClick,
        onTrackChartEvent,
        sizeAttributes,
        footerLabel,
        showFooterArrow = true,
        className = '',
        chartClassName = '',
    } = props;

    const showSlider = false;
    const showScrollbar = true;
    const scrollbarVisibleCount = props.scrollbarVisibleCount ?? 10;

    const theme = useTheme();
    const chartWrapperRef = useRef<HTMLDivElement | null>(null);
    const scrollbarTrackRef = useRef<HTMLDivElement | null>(null);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const scrollOffsetRef = useRef(0);
    const lastScrollTimeRef = useRef(0);
    const [chartWrapperWidth, setChartWrapperWidth] = useState(0);
    const [scrollOffset, setScrollOffset] = useState(0);
    const SCROLL_THROTTLE_MS = 150;

    useEffect(() => {
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return undefined;

        const updateWidth = () => {
            setChartWrapperWidth(wrapper.getBoundingClientRect().width);
        };
        updateWidth();

        if (typeof ResizeObserver === 'undefined') return undefined;

        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(wrapper);
        return () => resizeObserver.disconnect();
    }, []);

    const chartFilters = useStackedBarChartFilters(vizData);

    const chartConfig = useMemo(() => {
        if (!vizData || !chartFilters.normalizedVizData) return null;

        const normalized = chartFilters.normalizedVizData;
        const title = normalized.chartTitle || propTitle || '';
        const yAxisLabel = Array.isArray(normalized.yAxisTitle) ? normalized.yAxisTitle[0] : '';
        const xAxisTitleEntry =
            Array.isArray(normalized.xAxisTitle) && normalized.xAxisTitle.length > 0
                ? String(normalized.xAxisTitle[0] ?? '').trim()
                : '';
        const xAxisTitleLabel = xAxisTitleEntry || undefined;

        const legend = chartFilters.selectedZ.map((label, idx) => ({
            label,
            color: STACK_BAR_COLORS[idx % STACK_BAR_COLORS.length],
        }));

        return {
            title,
            yAxisLabel,
            xAxisTitleLabel,
            data: chartFilters.chartData,
            legend,
        };
    }, [vizData, propTitle, chartFilters.chartData, chartFilters.selectedZ, chartFilters.normalizedVizData]);

    const uniqueCategories = useMemo(() => {
        const rows = chartConfig?.data ?? [];
        const seen = new Set<string>();
        const ordered: string[] = [];
        for (const row of rows) {
            if (!seen.has(row.category)) {
                seen.add(row.category);
                ordered.push(row.category);
            }
        }
        return ordered;
    }, [chartConfig?.data]);
    const totalCategories = uniqueCategories.length;

    const [sliderRange, setSliderRange] = useState<[number, number]>([0, Math.max(0, totalCategories - 1)]);

    useEffect(() => {
        setSliderRange([0, Math.max(0, totalCategories - 1)]);
        setScrollOffset(0);
        scrollOffsetRef.current = 0;
    }, [totalCategories]);

    const filteredCategoryCount = sliderRange[1] - sliderRange[0] + 1;
    const estimatedPlotWidth =
        chartWrapperWidth - VERTICAL_CHART_WRAPPER_HORIZONTAL_PADDING - VERTICAL_PRIMARY_AXIS_WIDTH_ESTIMATE;
    const widthDerivedVisibleCategoryCount =
        estimatedPlotWidth > 0
            ? Math.max(1, Math.floor(estimatedPlotWidth / STACKED_FIXED_CATEGORY_STRIDE_PX))
            : Math.max(1, scrollbarVisibleCount);
    const effectiveScrollbarVisibleCount = widthDerivedVisibleCategoryCount;
    const needsScroll = showScrollbar && filteredCategoryCount > effectiveScrollbarVisibleCount;
    const maxScrollOffset = Math.max(0, filteredCategoryCount - effectiveScrollbarVisibleCount);
    const effectiveScrollOffset = Math.min(scrollOffset, maxScrollOffset);
    const visibleWindowStartIndex = needsScroll ? sliderRange[0] + effectiveScrollOffset : sliderRange[0];
    const visibleWindowEndIndex = needsScroll
        ? Math.min(visibleWindowStartIndex + effectiveScrollbarVisibleCount - 1, sliderRange[1])
        : sliderRange[1];
    const visibleWindowCategories = useMemo(
        () => new Set(uniqueCategories.slice(visibleWindowStartIndex, visibleWindowEndIndex + 1)),
        [uniqueCategories, visibleWindowStartIndex, visibleWindowEndIndex],
    );

    const { data, yAxisDomain, hasNegativeStackTotals } = useMemo(() => {
        const rows = chartConfig?.data ?? [];
        const enriched = enrichRowsWithStackTotals(rows);

        const segmentsByCategory = new Map<string, number[]>();
        for (const r of enriched) {
            const list = segmentsByCategory.get(r.category) ?? [];
            list.push(Number(r.value));
            segmentsByCategory.set(r.category, list);
        }

        let extentMin = Infinity;
        let extentMax = -Infinity;
        for (const values of segmentsByCategory.values()) {
            const { min, max } = g2AlignedStackExtentFromSegmentValues(values);
            extentMin = Math.min(extentMin, min);
            extentMax = Math.max(extentMax, max);
        }

        if (!Number.isFinite(extentMin) || !Number.isFinite(extentMax)) {
            return { data: enriched, yAxisDomain: undefined, hasNegativeStackTotals: false };
        }

        const hasNeg = extentMin < 0;
        const domain = computeSingleAxisDomain(extentMin, extentMax, STACKED_BAR_Y_AXIS_DOMAIN_PADDING);
        return { data: enriched, yAxisDomain: domain, hasNegativeStackTotals: hasNeg };
    }, [chartConfig?.data]);

    const visibleData = useMemo(
        () => (needsScroll ? data.filter((d) => visibleWindowCategories.has(d.category)) : data),
        [data, needsScroll, visibleWindowCategories],
    );

    const barsConfig: ChartConfig[] = useMemo(() => {
        if (!chartConfig || chartConfig.data.length === 0) return [];

        const colorRange = chartConfig.legend.map((l) => l.color);

        return [
            {
                encode: {
                    x: 'category',
                    y: 'value',
                    color: 'series',
                },
                scale: {
                    color: {
                        range: colorRange,
                        domain: chartConfig.legend.map((l) => l.label),
                    },
                    x: { padding: STACKED_X_SCALE_PADDING_RATIO },
                },
                transform: [{ type: 'stackY' }],
                style: {
                    maxWidth: STACKED_FIXED_BAR_WIDTH_PX,
                    minWidth: STACKED_FIXED_BAR_WIDTH_PX,
                    cursor: 'pointer',
                },
                labels: [
                    {
                        text: (d: StackedBarChartRowWithTotals) =>
                            d.showTotalLabel && !d.totalMarkerBelow
                                ? formatNumWithKMBTwoDecimalPlaces(d.stackTotal)
                                : '',
                        position: 'top',
                        textBaseline: 'bottom',
                        dy: -8,
                        fill: 'rgba(0, 0, 0, 0.65)',
                        fontSize: 12,
                        fontFamily: CHART_FONT_FAMILY_CSS,
                        transform: [{ type: 'overlapHide' }],
                    },
                    {
                        text: (d: StackedBarChartRowWithTotals) =>
                            d.showTotalLabel && d.totalMarkerBelow
                                ? formatNumWithKMBTwoDecimalPlaces(d.stackTotal)
                                : '',
                        position: 'bottom',
                        textBaseline: 'top',
                        dy: 8,
                        fill: 'rgba(0, 0, 0, 0.65)',
                        fontSize: 12,
                        fontFamily: CHART_FONT_FAMILY_CSS,
                        transform: [{ type: 'overlapHide' }],
                    },
                ],
                tooltip: {
                    title: (d: StackedBarChartRowWithTotals) => String(d.category ?? ''),
                    items: [
                        (d: StackedBarChartRowWithTotals) => {
                            const leg = chartConfig.legend.find((l) => l.label === d.series);
                            return {
                                name: String(d.series ?? ''),
                                value: formatNumWithKMB(d.value),
                                color: leg?.color ?? STACK_BAR_COLORS[0],
                            };
                        },
                    ],
                },
            },
        ];
    }, [chartConfig]);

    const viewLevelAdditionalProps = useMemo(
        () => ({
            theme: { fontFamily: CHART_FONT_FAMILY_CSS },
            scale: {
                y: {
                    ...(yAxisDomain ? { domain: yAxisDomain } : {}),
                },
            },
            axis: {
                x: {
                    // Fixed stacked-bar geometry reserves `STACKED_FIXED_CATEGORY_STRIDE_PX` per tick;
                    // size labels to that fixed space so every category stays visible and never collides.
                    labelFormatter: (text: string) =>
                        formatFixedSpacingXAxisLabel(text, STACKED_FIXED_CATEGORY_STRIDE_PX),
                    labelAutoHide: false,
                    labelAutoWrap: false,
                    labelAutoRotate: false,
                    ...(chartConfig?.xAxisTitleLabel
                        ? {
                              title: chartConfig.xAxisTitleLabel,
                              titleFontFamily: CHART_FONT_FAMILY_CSS,
                              titleFontSize: 14,
                              titleFontWeight: 400,
                              titleFill: 'rgba(0,0,0,0.45)',
                          }
                        : { title: false }),
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                    ...(hasNegativeStackTotals && {
                        crossPadding: 32,
                        labelSpacing: 12,
                    }),
                },
                y: {
                    labelFormatter: (value: number) => formatNumWithKMB(value),
                    title: false,
                    grid: true,
                    gridLineWidth: 1,
                    gridStroke: 'rgba(0, 0, 0, 0.06)',
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                },
            },
            legend: false,
            interaction: {
                ...getChartColumnHighlightInteraction(),
                tooltip: getAggregatedChartTooltipInteraction(),
                sliderFilter: { adaptiveMode: false },
                scrollbarFilter: { adaptiveMode: false },
            },
            clip: false,
        }),
        [yAxisDomain, hasNegativeStackTotals, chartConfig?.xAxisTitleLabel],
    );

    const chartTheme = useMemo(
        () => ({
            ...theme,
            components: {
                ...theme?.components,
                Select: {
                    ...(theme as { components?: { Select?: object } })?.components?.Select,
                    colorTextPlaceholder: 'rgba(0,0,0,0.7)',
                },
            },
        }),
        [theme],
    );

    const chartTitleForTracking = vizData?.chartTitle ?? propTitle;
    const chartTypeForTracking = vizData?.chart_type ?? 'StackBar';
    const trackChartEvent = onTrackChartEvent;

    const scrollToOffset = useCallback(
        (targetOffset: number) => {
            if (!needsScroll) return;

            const next = Math.max(0, Math.min(maxScrollOffset, Math.round(targetOffset)));
            const prev = scrollOffsetRef.current;
            if (next === prev) return;
            scrollOffsetRef.current = next;
            setScrollOffset(next);
        },
        [needsScroll, maxScrollOffset],
    );

    const scrollChart = useCallback(
        (delta: number) => {
            scrollToOffset(scrollOffsetRef.current + delta);
        },
        [scrollToOffset],
    );

    const scrollbarThumbRatio = needsScroll
        ? Math.min(1, effectiveScrollbarVisibleCount / Math.max(1, filteredCategoryCount))
        : 1;
    const scrollbarThumbWidthPercent = scrollbarThumbRatio * 100;
    const scrollbarThumbLeftPercent =
        maxScrollOffset > 0 ? (effectiveScrollOffset / maxScrollOffset) * (100 - scrollbarThumbWidthPercent) : 0;

    const handleScrollbarPointer = useCallback(
        (clientX: number) => {
            const track = scrollbarTrackRef.current;
            if (!track || maxScrollOffset <= 0) return;
            const rect = track.getBoundingClientRect();
            const thumbWidthPx = rect.width * scrollbarThumbRatio;
            const availableTrackPx = Math.max(1, rect.width - thumbWidthPx);
            const leftPx = Math.max(0, Math.min(availableTrackPx, clientX - rect.left - thumbWidthPx / 2));
            const nextOffset = (leftPx / availableTrackPx) * maxScrollOffset;
            const prev = scrollOffsetRef.current;
            scrollToOffset(nextOffset);
            trackChartEvent?.({
                type: 'scroll',
                chartTitle: chartTitleForTracking,
                chartType: chartTypeForTracking,
                direction: nextOffset > prev ? 'forward' : 'backward',
            });
        },
        [maxScrollOffset, scrollbarThumbRatio, scrollToOffset, trackChartEvent, chartTitleForTracking, chartTypeForTracking],
    );

    const handleScrollbarPointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.currentTarget.setPointerCapture?.(event.pointerId);
            handleScrollbarPointer(event.clientX);
        },
        [handleScrollbarPointer],
    );

    const handleScrollbarPointerMove = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if ((event.buttons & 1) !== 1) return;
            handleScrollbarPointer(event.clientX);
        },
        [handleScrollbarPointer],
    );

    const trackFilterValueChange = useCallback(
        (filter_name: string, prevValues: unknown[], nextValues: unknown[]) => {
            if (!trackChartEvent) return;
            const prev = new Set(prevValues as Array<string | number>);
            const next = new Set(nextValues as Array<string | number>);
            const added = [...next].filter((v) => !prev.has(v));
            const removed = [...prev].filter((v) => !next.has(v));
            if (added.length > 0) {
                trackChartEvent({
                    type: 'filter_select',
                    chartTitle: chartTitleForTracking,
                    chartType: chartTypeForTracking,
                    filter_name,
                    values: added,
                });
            }
            if (removed.length > 0) {
                trackChartEvent({
                    type: 'filter_deselect',
                    chartTitle: chartTitleForTracking,
                    chartType: chartTypeForTracking,
                    filter_name,
                    values: removed,
                });
            }
        },
        [trackChartEvent, chartTitleForTracking, chartTypeForTracking],
    );

    const trackFilterDropdownOpen = useCallback(
        (filter_name: string) => {
            trackChartEvent?.({
                type: 'filter_dropdown_open',
                chartTitle: chartTitleForTracking,
                chartType: chartTypeForTracking,
                filter_name,
            });
        },
        [trackChartEvent, chartTitleForTracking, chartTypeForTracking],
    );

    useEffect(() => {
        if (scrollOffsetRef.current > maxScrollOffset) {
            scrollOffsetRef.current = maxScrollOffset;
            setScrollOffset(maxScrollOffset);
        }
    }, [maxScrollOffset]);

    useEffect(() => {
        if (!needsScroll) return;
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return;

        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const now = Date.now();
            if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) return;
            lastScrollTimeRef.current = now;
            const rawDelta = e.deltaX || e.deltaY;
            const direction = rawDelta > 0 ? 1 : -1;
            scrollChart(direction);
            trackChartEvent?.({
                type: 'scroll',
                chartTitle: chartTitleForTracking,
                chartType: chartTypeForTracking,
                direction: direction > 0 ? 'forward' : 'backward',
            });
        };

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 1) {
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!touchStartRef.current || e.touches.length !== 1) return;
            const dx = touchStartRef.current.x - e.touches[0].clientX;
            const dy = touchStartRef.current.y - e.touches[0].clientY;
            if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
                e.preventDefault();
                const now = Date.now();
                if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) return;
                lastScrollTimeRef.current = now;
                const direction = dx > 0 ? 1 : -1;
                scrollChart(direction);
                trackChartEvent?.({
                    type: 'scroll',
                    chartTitle: chartTitleForTracking,
                    chartType: chartTypeForTracking,
                    direction: direction > 0 ? 'forward' : 'backward',
                });
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        };

        const onTouchEnd = () => {
            touchStartRef.current = null;
        };

        wrapper.addEventListener('wheel', onWheel, { passive: false });
        wrapper.addEventListener('touchstart', onTouchStart, { passive: true });
        wrapper.addEventListener('touchmove', onTouchMove, { passive: false });
        wrapper.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            wrapper.removeEventListener('wheel', onWheel);
            wrapper.removeEventListener('touchstart', onTouchStart);
            wrapper.removeEventListener('touchmove', onTouchMove);
            wrapper.removeEventListener('touchend', onTouchEnd);
        };
    }, [needsScroll, scrollChart, trackChartEvent, chartTitleForTracking, chartTypeForTracking]);

    if (!vizData || !chartFilters.normalizedVizData) return null;

    const title = chartConfig?.title ?? vizData.chartTitle ?? '';
    const legend = chartConfig?.legend ?? [];
    const yAxisLabel = chartConfig?.yAxisLabel ?? '';
    const hasZFilter = chartFilters.zOptions.length > 0;

    return (
        <ConfigProvider theme={chartTheme}>
            <div
                className={`w-full bg-white rounded-lg border border-[rgba(0,0,0,0.06)] ${className}`}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-asper-enhanced-stacked-barchart
                data-asper-enhanced-stacked-barchart-v2
            >
                <div className="p-5 border-b border-[rgba(0,0,0,0.06)]">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="font-['Open_Sans'] font-semibold text-[16px] leading-normal text-[rgba(0,0,0,0.88)] m-0">
                            {title}
                        </h3>
                        {onDownload && (
                            <Button
                                type="text"
                                icon={<DownloadFile size={16} />}
                                onClick={onDownload}
                                className="!p-2"
                                data-test-id={`${props['data-test-id']}-download-btn`}
                            />
                        )}
                    </div>

                    {description && (
                        <p className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] m-0">
                            {description}
                        </p>
                    )}
                </div>

                {showFilters && hasZFilter && (
                    <div className="px-5 pt-5 pb-3">
                        <ChartFilterBar>
                            <ChartFilterColumn label="Factors">
                                <Select
                                    mode="multiple"
                                    value={chartFilters.selectedZ}
                                    onDropdownVisibleChange={(open) => open && trackFilterDropdownOpen('Factors')}
                                    onChange={(value) => {
                                        trackFilterValueChange(
                                            'Factors',
                                            chartFilters.selectedZ,
                                            (value as string[]) || [],
                                        );
                                        chartFilters.updateZFilter(value as string[]);
                                    }}
                                    options={chartFilters.zOptions.map((z: string) => ({
                                        label: z,
                                        value: z,
                                    }))}
                                    tagRender={chartMultiSelectTagRender(chartFilters.selectedZ, (v) => v)}
                                    className={CHART_FILTER_SELECT_CLASSNAME}
                                    placeholder="Select factors..."
                                    data-test-id={`${props['data-test-id']}-filter-z`}
                                    dropdownRender={(menu) =>
                                        chartFilterDropdownRender(menu, {
                                            onReset: () => {
                                                trackFilterValueChange('Factors', chartFilters.selectedZ, []);
                                                chartFilters.updateZFilter([]);
                                            },
                                            onSelectAll: () => {
                                                trackFilterValueChange(
                                                    'Factors',
                                                    chartFilters.selectedZ,
                                                    chartFilters.zOptions,
                                                );
                                                chartFilters.updateZFilter(chartFilters.zOptions);
                                            },
                                            dataTestIdPrefix: `${props['data-test-id']}-filter-z`,
                                        })
                                    }
                                />
                            </ChartFilterColumn>
                        </ChartFilterBar>
                    </div>
                )}

                {legend.length > 0 && (
                    <div className="px-5 py-3 w-full min-w-0">
                        <ChartLegendOverflow
                            items={legend.map((item) => ({
                                key: item.label,
                                label: item.label,
                                color: item.color,
                            }))}
                            data-test-id={props['data-test-id'] ? `${props['data-test-id']}-legend` : undefined}
                        />
                    </div>
                )}

                {yAxisLabel && (
                    <div className="px-5 pt-3">
                        <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                            {yAxisLabel}
                        </p>
                    </div>
                )}

                <div ref={chartWrapperRef} className={`px-5 py-5 ${chartClassName}`}>
                    {data.length === 0 || barsConfig.length === 0 ? (
                        <ChartNoDataState minHeight={400} data-test-id={`${props['data-test-id']}-no-data`} />
                    ) : (
                        <StackedBarChart
                            data={visibleData}
                            barsConfig={barsConfig}
                            legend={[]}
                            sizeAttributes={{
                                height: 400,
                                paddingTop: STACKED_BAR_LABEL_PADDING_TOP,
                                insetTop: STACKED_BAR_LABEL_INSET_TOP,
                                paddingBottom:
                                    (showSlider && !showScrollbar ? 140 : 100) + (hasNegativeStackTotals ? 64 : 35),
                                ...(hasNegativeStackTotals && { insetBottom: 44 }),
                                paddingLeft: 80,
                                ...sizeAttributes,
                            }}
                            viewLevelAdditionalProps={viewLevelAdditionalProps}
                            barClickHandler={onBarClick}
                            data-test={`${props['data-test']}-chart`}
                        />
                    )}
                </div>

                {needsScroll && (
                    <div className="px-5 pb-4 flex justify-center" data-export-exclude>
                        <div
                            ref={scrollbarTrackRef}
                            role="scrollbar"
                            aria-orientation="horizontal"
                            aria-valuemin={0}
                            aria-valuemax={maxScrollOffset}
                            aria-valuenow={effectiveScrollOffset}
                            tabIndex={0}
                            className="relative h-2 rounded-full cursor-pointer select-none touch-none"
                            style={{
                                width: '38%',
                                minWidth: 112,
                                maxWidth: 360,
                                background: '#f0f0f0',
                            }}
                            onPointerDown={handleScrollbarPointerDown}
                            onPointerMove={handleScrollbarPointerMove}
                            onKeyDown={(event) => {
                                if (event.key === 'ArrowLeft') {
                                    event.preventDefault();
                                    scrollChart(-1);
                                } else if (event.key === 'ArrowRight') {
                                    event.preventDefault();
                                    scrollChart(1);
                                }
                            }}
                            data-test-id={`${props['data-test-id']}-v2-scroller`}
                        >
                            <div
                                className="absolute top-0 h-2 rounded-full"
                                style={{
                                    left: `${scrollbarThumbLeftPercent}%`,
                                    width: `${scrollbarThumbWidthPercent}%`,
                                    background: '#5B50D1',
                                    opacity: 0.45,
                                }}
                            />
                        </div>
                    </div>
                )}

                {footerLabel && (
                    <div className="px-5 pb-5 flex justify-center items-center gap-2">
                        <span className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                            {footerLabel}
                        </span>
                        {showFooterArrow && <ArrowRight size={16} color="rgba(0,0,0,0.88)" />}
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default EnhancedStackedBarChartV2;
